import { componentRegistry } from './ComponentRegistry';
import { EventSystem } from './EventSystem';
import { DiagramComponent, DiagramEventData } from './DiagramComponent';
import { schemaValidator } from '../validation/SchemaValidator';
import type { Variables, JXGBoard, DiagramConfig, ComponentConfig } from '../types/interfaces';

// Declare global JXG for JSXGraph
declare const JXG: {
  JSXGraph: {
    initBoard(containerId: string, options: object): JXGBoard;
    freeBoard(board: JXGBoard): void;
  };
  COORDS_BY_SCREEN: number;
  Coords: new (type: number, pos: number[], board: JXGBoard) => {
    usrCoords: number[];
  };
};

/** Canvas configuration */
interface CanvasConfig {
  width?: number;
  height?: number;
  boundingbox?: [number, number, number, number];
}

/** Extended diagram configuration with canvas */
interface BlitzDiagramConfig extends DiagramConfig {
  canvas?: CanvasConfig;
  showAxes?: boolean;
  variables?: Variables;
  components?: ComponentConfig[];
  debug?: boolean;
}

/**
 * BlitzDiagram Web Component
 * Custom HTML element that renders declarative JSON as interactive diagram
 */
export class BlitzDiagram extends HTMLElement {
  board: JXGBoard | null = null;
  rootComponent: DiagramComponent | null = null;
  eventSystem: EventSystem;
  variables: Variables = {};
  debug: boolean = false;

  constructor() {
    super();
    this.eventSystem = new EventSystem();
    this._ensureGlobalStyles();
  }

  /**
   * Called when element is added to DOM
   */
  connectedCallback(): void {
    // Defer initialization to allow innerHTML to be fully parsed
    // This is necessary when diagrams are inserted via innerHTML or template rendering
    setTimeout(() => {
      try {
        const config = this._getConfig();
        this.debug = config.debug === true;
        if (this.debug) {
          console.log('[BlitzDiagram] Initializing with config:', config);
        }
        this._initialize(config);
      } catch (error) {
        console.error('Failed to initialize diagram:', error);
        this._showError((error as Error).message);
      }
    }, 0);
  }

  /**
   * Called when element is removed from DOM
   */
  disconnectedCallback(): void {
    this._cleanup();
  }

  /**
   * Initialize diagram from config
   * @param config - Diagram configuration
   */
  private _initialize(config: BlitzDiagramConfig): void {
    // Create container div
    const container = document.createElement('div');
    container.id = this._generateId();
    container.style.width = '100%';
    container.style.height = '100%';
    this.appendChild(container);

    // Get canvas configuration
    const canvas = config.canvas || {
      width: 800,
      height: 600,
      boundingbox: [-10, 10, 10, -10] as [number, number, number, number],
    };

    // Initialize JSXGraph board
    this.board = JXG.JSXGraph.initBoard(container.id, {
      boundingbox: canvas.boundingbox,
      axis: config.showAxes !== false,
      showCopyright: false,
      showNavigation: false,
      keepaspectratio: true,  // Maintain aspect ratio - prevents distortion
      pan: { 
        enabled: false  // Disable board panning to allow object dragging
      },
      zoom: { enabled: false },
    });

    // Set initial variables
    this.variables = config.variables || {};

    // Create root component (diagram itself is a component)
    this.rootComponent = this._createComponentTree(
      { ...config, type: config.type || 'diagram' } as ComponentConfig,
      this.variables
    );

    // Render
    if (this.rootComponent) {
      this.rootComponent.render();
    }

    // DON'T call board.update() here - it suspends isDraggable on movable points
    // Each component that needs update will call it individually

    // Emit ready event
    this.dispatchEvent(new CustomEvent('diagram-ready', {
      detail: { board: this.board }
    }));
  }

  /**
   * Inject global styles used by the interactive diagram once per document
   */
  private _ensureGlobalStyles(): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (document.getElementById('blitz-diagram-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'blitz-diagram-styles';
    style.textContent = `
      blitz-diagram .jxg-text-no-pointer,
      blitz-diagram .jxg-grid-line,
      blitz-diagram .jxg-grid-point {
        pointer-events: none !important;
      }
    `;
    document.head?.appendChild(style);
  }

  /**
   * Create component tree from config
   * @param config - Component configuration
   * @param state - Initial state
   * @returns Created component or null
   */
  private _createComponentTree(config: ComponentConfig, state: Variables): DiagramComponent | null {
    if (!this.board) return null;
    
    const component = componentRegistry.create(config, this.board, state);
    
    if (!component) {
      return null;
    }

    // Pass debug flag to component
    component.debug = this.debug;
    
    if (this.debug) {
      console.log('[BlitzDiagram] Created component:', component.type, component.id, 'config:', config);
    }

    // Setup event propagation
    component.onEvent = (event: DiagramEventData) => {
      this._handleComponentEvent(event);
    };

    // Create children
    const configWithComponents = config as BlitzDiagramConfig;
    if (configWithComponents.components) {
      configWithComponents.components.forEach(childConfig => {
        const child = this._createComponentTree(childConfig, component.getState());
        if (child) {
          component.addChild(child);
        }
      });
    }

    return component;
  }

  /**
   * Setup JSXGraph board event handlers
   * Note: Currently disabled as they interfere with JSXGraph native dragging
   */
  private _setupBoardEvents(): void {
    if (!this.board) return;
    
    // Mouse down - initiate hit testing
    this.board.on('down', (evt: Event) => {
      const coords = this._getCoords(evt);
      if (this.debug) {
        console.log('[Board] down at', coords);
      }
      this._handleBoardEvent('mousedown', coords);
    });

    // Mouse move - for hover, drag
    this.board.on('move', (evt: Event) => {
      const coords = this._getCoords(evt);
      if (this.debug) {
        console.log('[Board] move at', coords);
      }
      this._handleBoardEvent('mousemove', coords);
    });

    // Mouse up - end drag, click
    this.board.on('up', (evt: Event) => {
      const coords = this._getCoords(evt);
      if (this.debug) {
        console.log('[Board] up at', coords);
      }
      this._handleBoardEvent('mouseup', coords);
    });
  }

  /**
   * Handle board-level event (mouse, touch)
   * @param type - Event type
   * @param coords - Coordinates in board space
   */
  private _handleBoardEvent(type: string, coords: { x: number; y: number }): void {
    // Hit test to find target component
    const target = this._hitTest(coords.x, coords.y);
    
    if (target) {
      const event = this.eventSystem.createEvent(type, coords, target);
      
      // Build bubble path from target to root
      const bubblePath = this._buildBubblePath(target);
      
      // Emit with bubbling
      this.eventSystem.emit(event, bubblePath);
    }
  }

  /**
   * Handle component event
   * @param event - Event data
   */
  private _handleComponentEvent(event: DiagramEventData): void {
    if (this.debug) {
      console.log('[BlitzDiagram] Component event:', event.type, event.detail);
    }

    // Re-emit as custom DOM event
    this.dispatchEvent(new CustomEvent(event.type, {
      detail: event.detail,
      bubbles: true,
    }));

    // Handle special events
    if (event.type === 'variable-change') {
      if (this.debug) {
        console.log('[BlitzDiagram] Variable change:', event.detail.name, '=', event.detail.value);
      }
      this.variables[event.detail.name] = event.detail.value;
      
      // Update all components to reflect variable change
      if (this.rootComponent) {
        if (this.debug) {
          console.log('[BlitzDiagram] Updating component tree');
        }
        this._updateComponentTree(this.rootComponent);
      }
    }
  }

  /**
   * Recursively update component tree with new variables
   * @param component - Component to update
   */
  private _updateComponentTree(component: DiagramComponent): void {
    if (this.debug) {
      console.log('[BlitzDiagram] Updating component:', component.type, component.id);
    }
    
    // Pass debug flag to component
    component.debug = this.debug;
    
    // Update component with new state
    component.parentState = this.variables;
    
    // Avoid destroying/recreating:
    // - Top-level container (diagram/2d/3d)
    // - Interactive elements that manage their own state (slider)
    // All other components (tenframe, text, gridlayout, etc.) should update
    if (!['diagram', '2d', '3d', 'slider'].includes(component.type)) {
      component.update(this.variables);
    }
    
    // Update children (but gridlayout already updates its own children)
    if (component.type !== 'gridlayout') {
      component.children.forEach(child => this._updateComponentTree(child));
    }
  }

  /**
   * Hit test - find most specific component at (x, y)
   * @param x - X coordinate
   * @param y - Y coordinate
   * @returns Most specific component at coordinates or null
   */
  private _hitTest(x: number, y: number): DiagramComponent | null {
    const hits: DiagramComponent[] = [];
    
    const testComponent = (component: DiagramComponent): void => {
      if (component.hitTest(x, y)) {
        hits.push(component);
      }
      component.children.forEach(child => testComponent(child));
    };

    if (this.rootComponent) {
      testComponent(this.rootComponent);
    }

    // Sort by specificity (highest first)
    hits.sort((a, b) => b.getSpecificity() - a.getSpecificity());

    return hits[0] || null;
  }

  /**
   * Build bubble path from component to root
   * @param component - Starting component
   * @returns Array of components from target to root
   */
  private _buildBubblePath(component: DiagramComponent): DiagramComponent[] {
    const path: DiagramComponent[] = [component];
    
    // Walk up tree (simplified - would need parent references)
    // For now, just return target + root
    if (this.rootComponent && component !== this.rootComponent) {
      path.push(this.rootComponent);
    }

    return path;
  }

  /**
   * Get board coordinates from mouse event
   * @param evt - Mouse/touch event
   * @returns Coordinates in board space
   */
  private _getCoords(evt: Event): { x: number; y: number } {
    if (!this.board) return { x: 0, y: 0 };
    
    const pos = (this.board as any).getMousePosition(evt);
    const coords = new JXG.Coords(JXG.COORDS_BY_SCREEN, pos, this.board);
    return {
      x: coords.usrCoords[1],
      y: coords.usrCoords[2],
    };
  }

  /**
   * Get configuration from data attribute or child script tag
   * @returns Parsed configuration
   */
  private _getConfig(): BlitzDiagramConfig {
    let config: any;

    // Try data-config attribute
    const dataConfig = this.getAttribute('data-config');
    if (dataConfig) {
      // Some template/rendering layers can double curly braces ("{" -> "{{"}).
      // Normalize by collapsing any doubled braces before parsing as JSON.
      const normalizedConfig = dataConfig.replace(/{{/g, '{').replace(/}}/g, '}');

      try {
        config = JSON.parse(normalizedConfig);
      } catch (e) {
        console.error('[BlitzDiagram] data-config parse failed:', e);
        console.error('[BlitzDiagram] Full value:', dataConfig);
        console.error('[BlitzDiagram] Normalized value:', normalizedConfig);
        throw e;
      }
    } else {
      // Try script tag child
      const script = this.querySelector('script[type="application/json"]');
      if (script) {
        const jsonText = script.textContent || '{}';
        try {
          config = JSON.parse(jsonText);
        } catch (e) {
          console.error('[BlitzDiagram] JSON parse failed:', e);
          console.error('[BlitzDiagram] Full JSON:', jsonText);
          throw e;
        }
      } else {
        throw new Error('No configuration found (data-config or script tag)');
      }
    }

    // Validate schema before returning
    try {
      schemaValidator.validateOrThrow(config);
      // After validation, config is guaranteed to match BlitzDiagramConfig
      return config;
    } catch (error) {
      console.error('[BlitzDiagram] Schema validation failed:', error);
      throw new Error(`Invalid diagram configuration: ${(error as Error).message}`);
    }
  }

  /**
   * Cleanup resources
   */
  private _cleanup(): void {
    if (this.rootComponent) {
      this.rootComponent.destroy();
      this.rootComponent = null;
    }

    if (this.board) {
      JXG.JSXGraph.freeBoard(this.board);
      this.board = null;
    }

    this.eventSystem.clear();
  }

  /**
   * Show error message
   * @param message - Error message to display
   */
  private _showError(message: string): void {
    this.innerHTML = `
      <div style="padding: 20px; background: #fee; border: 1px solid #c00; border-radius: 4px;">
        <strong>Error loading diagram:</strong><br>
        ${message}
      </div>
    `;
  }

  /**
   * Generate unique ID
   * @returns Generated ID
   */
  private _generateId(): string {
    return `diagram_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Public API

  /**
   * Get variable value
   * @param name - Variable name
   * @returns Variable value
   */
  getVariable(name: string): string | number | boolean | undefined {
    return this.variables[name];
  }

  /**
   * Get multiple variables
   * @param names - Variable names
   * @returns Object with requested variables
   */
  getVariables(names: string[]): Variables {
    const result: Variables = {};
    names.forEach(name => {
      result[name] = this.variables[name];
    });
    return result;
  }

  /**
   * Set variable value
   * @param name - Variable name
   * @param value - Variable value
   */
  setVariable(name: string, value: string | number | boolean): void {
    this.variables[name] = value;
    if (this.rootComponent) {
      this.rootComponent.setVariable(name, value);
    }
  }

  /**
   * Get current diagram state
   * @returns Current state with variables
   */
  getState(): { variables: Variables } {
    return {
      variables: { ...this.variables },
    };
  }
}

// Note: Custom element registration happens in index.js after all component registrations
